"use client";

import React from "react";
import { ArrowRight, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const labelingCategories = [{
  title: "Equipment & machine nameplates",
  description: "Engraved ID and rating nameplates for machines, motors, and process equipment.",
  link: "/products/engraved-nameplates/equipment-nameplates/"
}, {
  title: "Asset tags",
  description: "Numbered, serialized, and barcoded asset tags that feed a maintenance or CMMS program.",
  link: "/products/engraved-nameplates/asset-tags/"
}, {
  title: "Panel nameplates & schedules",
  description: "Control panel, MCC, and breaker identification.",
  link: "/products/engraved-nameplates/panel-nameplates/"
}, {
  title: "Pipe & conduit markers",
  description: "Process and utility line identification for the plant.",
  link: "/products/pipe-conduit-markers/"
}, {
  title: "Arc flash labels",
  description: "Produced from your study data for MCCs and electrical rooms.",
  link: "/products/arc-flash-labels/"
}];
export default function WhatGetsLabeledOnAPlantFloor() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="what-gets-labeled-on-a-plant-floor" className="bg-[var(--color-logo)] py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
            What Does Evermark Label in a Manufacturing Plant?
          </h2>
          <p className="text-lg md:text-xl text-white/80 font-sans">
            The plant-wide identification Evermark Industrial Solutions produces:
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {labelingCategories.map((category, i) => {
          const cardContent = <Card className="h-full bg-secondary border-white/10 hover:border-primary/50 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/10 group-hover:-translate-y-1">
                <CardHeader className="p-6 md:p-8 h-full flex flex-col justify-center">
                  <CardTitle className="font-default text-xl text-white mb-3 flex items-start justify-between gap-4 group-hover:text-primary transition-colors">
                    <span>{category.title}</span>
                    <ArrowRight className="size-5 shrink-0 text-primary opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 mt-0.5" />
                  </CardTitle>
                  <CardDescription className="text-white/70 text-base font-sans leading-relaxed">
                    {category.description}
                  </CardDescription>
                </CardHeader>
              </Card>;
          if (i === 0 || i === 1 || i === 2 || i === 3 || i === 4) {
            return <div key={i} data-index={i} className="block h-full group rounded-xl">
                  {cardContent}
                </div>;
          }
          return <Link key={i} to={category.link} data-index={i} className="block h-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                {cardContent}
              </Link>;
        })}
        </div>

        <div className={`mt-16 pt-8 border-t border-white/10 max-w-4xl transition-all duration-700 ease-out delay-500 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-start gap-3 text-white/60 bg-white/5 p-4 rounded-lg">
            <Info className="size-5 shrink-0 text-primary mt-0.5" />
            <p className="text-sm md:text-base font-sans leading-relaxed">
              Safety and warning labels are produced to your specified content
              and the applicable ANSI Z535 format — see the safety section
              below.
            </p>
          </div>
        </div>
      </div>
    </section>;
}